import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import {
  MatchedThermostat,
  THERMOSTAT_TEMPERATURE_LIMITS,
  ThermostatPreset,
  ThermostatPresetType,
} from '@app/modules/smart-devices/data/smart-devices.interface';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject, Observable, of, Subject, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  finalize,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { FanMode, HvacMode, PresetType, SmartDevicesService } from '../../service/smart-devices.service';

@Component({
  selector: 'device-thermostat-preset-detail',
  templateUrl: 'device-thermostat-preset-detail.component.html',
  styleUrls: ['device-thermostat-preset-detail.component.scss'],
})
export class DeviceThermostatPresetDetailComponent implements OnInit, OnDestroy {
  preset$: Observable<{ data: any; loading: boolean; error: any }>;
  presetId: string;

  saveStatus = 0;

  hasWriteAccess$ = this.permissionsService.hasAccessTo(Features.PERM_SMARTLOCKS_DASHBOARD_WRITE);

  allThermostats$ = this.smartService.getThermostats(0, '', [], 1000).pipe(map((res) => res.data));

  private saving$ = new Subject<ThermostatPreset>();
  private destroy$ = new Subject<void>();

  readonly hvacModes = Object.values(HvacMode);
  readonly fanModes = Object.values(FanMode);

  presetTypes = [
    {
      label: 'Occupied',
      value: PresetType.HOSPITABLE_OCCUPIED,
      description: 'Sets a comfortable temperature for guest stays, applied %timing% before check-in.',
    },
    {
      label: 'Unoccupied',
      value: PresetType.HOSPITABLE_UNOCCUPIED,
      description:
        'An energy-saving mode applied immediately at check-out and in between guests. While in this mode, if thermostat is manually adjusted, the system will allow a 2-hour override before reverting back.',
    },
    {
      label: 'Long-term Unoccupied',
      value: PresetType.HOSPITABLE_LONG_TERM_UNOCCUPIED,
      description: 'A deeper energy-saving mode applied at check-out if more than 2 week gap between reservations',
    },
  ];
  copying: boolean;
  deleting: boolean;

  readonly THERMOSTAT_TEMPERATURE_LIMITS = THERMOSTAT_TEMPERATURE_LIMITS;
  public selectedPresetType$ = new BehaviorSubject<ThermostatPresetType>(null);

  constructor(
    private route: ActivatedRoute,
    private smartService: SmartDevicesService,
    private permissionsService: UserPermissionsService,
    private titleService: TitleService,
    private router: Router,
    private toast: ToastNotificationsService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.presetId = this.route.snapshot.params['id'];
    this.hydratePreset();
    this.handleSaving();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleSaving() {
    this.saving$
      .pipe(
        debounceTime(500),
        tap(() => {
          this.saveStatus = 1; // Saving
        }),
        switchMap((preset) => this.smartService.updateThermostatPreset(this.presetId, preset)),
        catchError((error) => {
          this.saveStatus = -1; // Error
          return timer(5000).pipe(take(1));
        }),
        tap(() => {
          this.saveStatus = 2; // Saved
        }),
        delay(2000), // Delay before clearing save status
        finalize(() => {
          if (this.saveStatus === 1) {
            this.saveStatus = 0; // Reset if request was cancelled
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.saveStatus = 0;
        },
        complete: () => {
          this.saveStatus = 0;
        },
      });
  }

  hydratePreset() {
    this.preset$ = this.smartService.getThermostatPreset(this.presetId).pipe(
      map((res) => {
        this.titleService.manuallySet(res.name);
        this.selectedPresetType$.next(res.type);
        return { data: res, loading: false, error: false };
      }),
      startWith({ data: null, loading: true, error: false }),
      catchError((e) => of({ data: null, loading: false, error: 'Error fetching thermostat preset' }))
    );
  }

  updatePreset(preset: ThermostatPreset) {
    this.selectedPresetType$.next(preset.type);
    this.saving$.next(preset);
  }

  updateThermostatSelection(data: ThermostatPreset, thermostat: MatchedThermostat, checked: boolean) {
    if (checked) {
      data.thermostats.push(thermostat);
    } else {
      data.thermostats = data.thermostats.filter((t) => t.id !== thermostat.id);
    }
    this.saving$.next(data);
  }

  deletePrompt(preset: ThermostatPreset) {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: preset.name,
        action: 'Delete',
        additionalText: '',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deletePreset();
      }
    });
  }

  deletePreset() {
    this.deleting = true;
    this.smartService.deleteThermostatPreset(this.presetId).subscribe(
      () => {
        this.deleting = false;
        this.router.navigate(['/devices/thermostat-presets']);
      },
      (error) => {
        this.deleting = false;
        this.toast.open('Unable to delete preset', 'OK', NotificationType.Error);
      }
    );
  }

  copyPreset() {
    this.copying = true;
    this.smartService.copyThermostatPreset(this.presetId).subscribe(
      (res) => {
        this.copying = false;
        this.router.navigate(['/devices/thermostat-presets', res.data.id]);
      },
      (error) => {
        this.toast.open('Unable to copy preset', 'OK', NotificationType.Error);
        this.copying = false;
      }
    );
  }

  validateAndUpdateTemp(value: number, type: 'heating' | 'cooling', preset: ThermostatPreset): void {
    const validValue = Math.min(
      Math.max(value, THERMOSTAT_TEMPERATURE_LIMITS.MIN_FAHRENHEIT),
      THERMOSTAT_TEMPERATURE_LIMITS.MAX_FAHRENHEIT
    );

    if (type === 'heating') {
      preset.heating_set_point_fahrenheit = validValue;
    } else {
      preset.cooling_set_point_fahrenheit = validValue;
    }

    if (
      value >= THERMOSTAT_TEMPERATURE_LIMITS.MIN_FAHRENHEIT &&
      value <= THERMOSTAT_TEMPERATURE_LIMITS.MAX_FAHRENHEIT
    ) {
      this.updatePreset(preset);
    }
  }
}
